/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    BehaviorSubject,
    combineLatest,
    Subject,
    withLatestFrom,
} from 'rxjs';

import {
    map,
    take,
    takeUntil,
} from 'rxjs/operators';

import { L10nService } from '@vmw/ngx-vip';
import {
    AviDataGridFieldVisibility,
    IAviDataGridConfig,
    IAviDataGridConfigField,
    IAviDataGridRow,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import {
    IAviDynamicDataGridSortingInfo,
    TAviDataGridPaginationInfo,
} from 'ng/modules/data-grid/components/avi-dynamic-data-grid/avi-dynamic-data-grid.types';
import { RecommendationDialogService } from 'ng/modules/logs/services/recommendation-dialog.service';
import { AviDatePipe } from 'ng/modules/core/pipes/avi-date.pipe';

import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { StringService } from 'string-service';

import {
    IApplicationLog,
    IConnectionLog,
    WafStatusCode,
} from 'generated-types';

import * as globalL10n from 'global-l10n';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';

import {
    DurationProperty,
    FilterOperatorType,
    IFilterOption,
    TLogListStateParams,
    VsAppProfileType,
} from '../../../logs.types';

import { TTrafficLog } from '../traffic-logs.types';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';
import { TrafficLogListStore } from './traffic-log-list.store';

import { LogCinematicService } from '../../../services/log-cinematic.service';

import { LogsDurationDetailService } from '../../../services/logs-duration-detail.service';
import { LogsDownloadService } from '../../../services/logs-download.service';
import { LogsRefreshService } from '../../../services/logs-refresh.service';
import { getTrafficLogIdentifier } from '../utils/traffic-logs.utils';

import * as l10n from './traffic-log-list.l10n';
import './traffic-log-list.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *     Traffic logs list component for Application and Connection logs.
 *     Must be wrapped by a more specific log list component to provide the particular openCinematic
 *     callback.
 * @author Akul Aggarwal, Alex Klyuev, Zhiqian Liu, Shanmukha Sarath Kondiparthi
 */
@Component({
    selector: 'traffic-log-list',
    templateUrl: './traffic-log-list.component.html',
})
export class TrafficLogListComponent implements OnInit, OnDestroy, AfterViewInit {
    /**
     * The ID of the pool used to filter logs from a VS.
     * If not provided, all logs from the VS will be fetched.
     */
    @Input()
    public poolId?: string;

    /**
     * Callback from the parent log list component to open a specific kind of log cinematic view, eg. PoolLogCinematic.
     */
    @Output()
    public openLogCinematic = new EventEmitter<void>();

    // ---------------------- TemplateRefs for List Fields ----------------------

    @ViewChild('wafFieldTemplateRef') public wafFieldTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('clientIpTemplateRef') public clientIpTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('clientPortTemplateRef') public clientPortTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('serverIpTemplateRef') public serverIpTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('serverPortTemplateRef') public serverPortTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('uriTemplateRef') public uriTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('protocolTemplateRef') public protocolTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('dnsRequestTypeTemplateRef')
    public dnsRequestTypeTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('domainNameTemplateRef') public domainNameTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('dnsResponseTemplateRef') public dnsResponseTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('requestTemplateRef') public requestTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('responseTemplateRef') public responseTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('lengthTemplateRef') public lengthTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('totalBytesTemplateRef') public totalBytesTemplateRef: TemplateRef<HTMLElement>;
    @ViewChild('ednsSubnetTemplateRef') public ednsSubnetTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('durationTemplateRef')
    public readonly durationTemplateRef: TemplateRef<HTMLElement>;

    @ViewChild('durationColumnFilterTemplateRef')
    public readonly durationColumnFilterTemplateRef: TemplateRef<HTMLElement>;

    /**
     * TemplateRef for an expanded log summary content.
     */
    @ViewChild('expandedSummaryContentTemplateRef')
    public expandedSummaryContentTemplateRef: TemplateRef<HTMLElement>;

    public readonly l10nKeys = l10nKeys;

    public readonly FilterOperatorType = FilterOperatorType;

    public logsListGridConfig: IAviDataGridConfig;

    public isLogDurationDetailVisible = true;

    public readonly logListPaginationInfo$ = this.trafficLogsStore.logListPaginationInfo$;
    public readonly recentlyViewedLogIndex$ = this.trafficLogListStore.recentlyViewedLogIndex$;
    public readonly recentlyViewedLog$ = this.trafficLogListStore.recentlyViewedLog$;

    public readonly listLogCount$ = this.trafficLogsStore.listLogCount$;

    public readonly largestTotalTime$ = this.trafficLogListStore.logList$.pipe(
        map(logs => this.logsDurationDetailService.getLargestTotalTime(logs)),
    );

    public selectedFilterOption: IFilterOption = {
        label: globalL10nKeys.totalTimeLabel,
        property: DurationProperty.totalTime,
    };

    public readonly durationFilterOptions: IFilterOption[] = [
        {
            label: l10nKeys.clientRttLabel,
            property: DurationProperty.clientRtt,
        },
        {
            label: l10nKeys.serverRttLabel,
            property: DurationProperty.serverRtt,
        },
        {
            label: l10nKeys.wafLabel,
            customProperty: DurationProperty.waf,
        },
        {
            label: l10nKeys.appResponseLabel,
            property: DurationProperty.appResponseTime,
        },
        {
            label: l10nKeys.dataTransferLabel,
            property: DurationProperty.dataTransferTime,
        },
        this.selectedFilterOption,
    ];

    private readonly selectedFilterLabelSubject$: BehaviorSubject<string>;

    private readonly componentDestroySubject$ = new Subject<void>();

    constructor(
        private readonly aviDatePipe: AviDatePipe,
        @Inject('$filter')
        public readonly timeFormatterService: any,
        private readonly l10nService: L10nService,
        private readonly logCinematicService: LogCinematicService,
        private readonly recommendationDialogService: RecommendationDialogService,
        public readonly trafficLogListStore: TrafficLogListStore,
        public readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        public readonly schemaService: SchemaService,
        public readonly stringService: StringService,
        logsRefreshService: LogsRefreshService,
        logsDownloadService: LogsDownloadService,
        public readonly logsDurationDetailService: LogsDurationDetailService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        // Subscribe to Refresh and Download services for associated actions.
        logsRefreshService.refresh$.subscribe(({ isTableCustomTimeframe }) => {
            if (isTableCustomTimeframe) {
                trafficLogListStore.refreshList();
            }
        });

        // Subscribe to Download services.
        logsDownloadService.downloadCurrentPage$.subscribe(
            () => trafficLogListStore.downloadCurrentPage(),
        );

        logsDownloadService.downloadSelectedLogs$.subscribe(
            () => trafficLogListStore.downloadSelectedLogs(),
        );

        const selectedFilterLabel = this.l10nService.getMessage(this.selectedFilterOption.label);

        this.selectedFilterLabelSubject$ = new BehaviorSubject<string>(selectedFilterLabel);
    }

    /** @override */
    public ngOnInit(): void {
        /**
         * Subscribe to changes in the logs API parameters. Get new logs data upon change.
         */
        // subscribe to the store under ngOnInit instead of the constructor to avoid the unnecessary API call triggered
        // by the emission before the store is first lazy initialized, which happens under the page component ngOnInit
        this.trafficLogsStore.logListApiParams$
            .pipe(takeUntil(this.componentDestroySubject$))
            .subscribe((apiParams: TLogListStateParams) => {
                // add the pool ID to fetch pool logs data
                // we avoid directly updating api params in the store since it's not a user filter to show in the
                // search bar
                this.trafficLogListStore.getLogList(
                    addPoolIdFilterParams({ ...apiParams }, this.poolId),
                );
            });

        /**
         * The currently / recently viewed log can be set in two ways:
         * 1. when opening a log entry c-view from the list view
         * 2. when switching between logs from the c-view.
         * In both ways the log is set by its index in the list.
         */
        combineLatest([
            this.recentlyViewedLogIndex$,
            this.trafficLogListStore.logList$,
        ])
            .pipe(takeUntil(this.componentDestroySubject$))
            .subscribe(([logIndex, logList]) => {
                if (logIndex > -1) {
                    const log = logList ? logList[logIndex] : null;

                    this.recentlyViewedLog$.next(log);
                } else {
                    this.recentlyViewedLog$.next(null);
                }
            });
    }

    /**
     * @override
     * Complete data grid config once templateRefs have initialized.
     */
    public ngAfterViewInit(): void {
        // Subscribe to properties that affect the data grid.
        combineLatest([
            this.trafficLogsStore.vsAppProfileType$,
            this.trafficLogsStore.hasWafPolicy$,
            this.trafficLogsStore.hasTcpProxyNetProfile$,
        ])
            .pipe(takeUntil(this.componentDestroySubject$))
            .subscribe(
                ([
                    vsAppProfileType,
                    hasWafPolicy,
                    hasTcpProxyNetProfile,
                ]: [VsAppProfileType, boolean, boolean]) => {
                    this.initDataGrid(hasWafPolicy);
                    this.setDataGridFields(vsAppProfileType, hasWafPolicy, hasTcpProxyNetProfile);
                },
            );
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.trafficLogListStore.cancelLogListRequest();

        this.componentDestroySubject$.next();
        this.componentDestroySubject$.complete();
        this.selectedFilterLabelSubject$.complete();

        if (this.recommendationDialogService.isOpen()) {
            this.recommendationDialogService.closeDialog();
        }
    }

    /**
     * Handle the user changing the table size or page.
     */
    public handlePaginationChange(pageInfo: TAviDataGridPaginationInfo): void {
        this.trafficLogsStore.setTablePaginationProps(pageInfo.current, pageInfo.size);
    }

    /**
     * Update the store with log_id's that the user has selected.
     */
    public handleSelectionChange(rows: IAviDataGridRow[]): void {
        const logIds = rows.map(row => getTrafficLogIdentifier(row as TTrafficLog));

        this.trafficLogsStore.setSelectedLogs(logIds);
    }

    /**
     * Read sorting information upon grid state changes and perform sorting calls accordingly.
     * The list are returned sorted descendingly by default when no custom sortings are applied.
     */
    public handleSortingChange(sortingInfo: IAviDynamicDataGridSortingInfo): void {
        const { sortBy, reverse: descending } = sortingInfo;
        const secondarySortBy = 'report_timestamp';
        let sortByTerm = `${descending ? '-' : ''}${sortBy}`;

        // set 'report_timestamp' as a secondary sorting term, which means that when the list is
        // sorted by terms other than timestamp, entries sharing the same values of the
        // sorted property are then arranged by the timestamp, descendingly
        if (sortBy !== secondarySortBy) {
            sortByTerm += `,-${secondarySortBy}`;
        }

        this.trafficLogsStore.setTableSortingProps(sortByTerm);
    }

    /**
     * Get edns subnet value from a log.
     */
    // eslint-disable-next-line class-methods-use-this
    public getEdnsSubnet(
        log: IConnectionLog,
    ): { value: number | string, type: string } | undefined {
        if (
            log.dns_request &&
            log.dns_request.opt_record &&
            log.dns_request.opt_record.options
        ) {
            const { options } = log.dns_request.opt_record;

            const { subnet_ip6: subnetIp6, subnet_ip: subnetIp } = options[0];

            if (subnetIp6) {
                return {
                    value: subnetIp6,
                    type: 'dns_request.opt_record.options.subnet_ip6',
                };
            }

            if (subnetIp) {
                return {
                    value: subnetIp,
                    type: 'dns_request.opt_record.options.subnet_ip',
                };
            }
        }
    }

    /**
     * Get response code description.
     */
    public getResponseCodeDescription(code: number): string {
        if (!code) {
            return '';
        }

        const description = this.schemaService.getEnumValue(
            'HTTPResponseCodes',
            `HTTP_RESPONSE_CODE_${code}`,
        )?.label || '';

        return description;
    }

    public onDurationFilterOptionClick(filter: IFilterOption): void {
        this.selectedFilterOption = filter;
        this.selectedFilterLabelSubject$.next(this.l10nService.getMessage(filter.label));
    }

    public getCustomPropertyTime(log: IApplicationLog, customProperty: string): number | undefined {
        if (customProperty === DurationProperty.waf) {
            return this.logsDurationDetailService.getWafTime(log);
        }
    }

    public onDurationDetailHover(): void {
        this.isLogDurationDetailVisible = true;
    }

    public onDurationDetailValueHover(event: MouseEvent): void {
        event.stopPropagation();
        this.isLogDurationDetailVisible = false;
    }

    /**
     * Callback to ngFor-track by.
     */
    // eslint-disable-next-line class-methods-use-this
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Open the cinematic view of a log entry.
     */
    public viewFullLog(log: TTrafficLog): void {
        this.trafficLogListStore.logList$
            .pipe(take(1))
            .subscribe(logList => {
                const index = logList.indexOf(log);

                this.recentlyViewedLogIndex$.next(index);
            });

        // list row of the log entry should be highlighted upon closing its c-view
        this.logCinematicService.logCinematicCloseSubject$
            .pipe(
                withLatestFrom(this.recentlyViewedLog$),
                take(1),
            )
            .subscribe(
                ([_, recentlyViewedLog]) =>
                    recentlyViewedLog && this.highlightRowForCinematicView(recentlyViewedLog),
            );

        this.openLogCinematic.emit();
    }

    /**
     * Initialize data grid.
     */
    private initDataGrid(hasWafPolicy: boolean): void {
        const singleactions = [
            {
                label: this.l10nService.getMessage(globalL10nKeys.downloadLabel),
                shape: 'download',
                onClick: (log: TTrafficLog) => {
                    const { log_id: logId, service_engine: seId } = log;

                    this.trafficLogsStore.downloadLogEntry({
                        logId,
                        seId,
                    });
                },
            },
            {
                label: this.l10nService.getMessage(l10nKeys.detailIconLabel),
                shape: 'eye',
                onClick: (log: TTrafficLog) => this.viewFullLog(log),
            },
        ];

        // add recommendation only for WAF VS
        if (hasWafPolicy) {
            let vsUuid: string;

            this.trafficLogsStore.vsUuid$
                .pipe(take(1))
                .subscribe(id => vsUuid = id);

            const recommendationAction = {
                label: this.l10nService.getMessage(l10nKeys.recommendationLabel),
                shape: 'recommendation',
                onClick: (log: IApplicationLog) => {
                    const {
                        report_timestamp: reportTimestamp,
                        request_id: requestId,
                    } = log;

                    this.recommendationDialogService.openDialog(vsUuid, reportTimestamp, requestId);
                },
                disabled: (log: IApplicationLog) => {
                    const { waf_log: wafLog } = log;

                    if (wafLog) {
                        const { status } = wafLog;

                        return status !== WafStatusCode.FLAGGED &&
                            status !== WafStatusCode.REJECTED;
                    }

                    return true;
                },
            };

            singleactions.unshift(recommendationAction);
        }

        this.logsListGridConfig = {
            getUniqueRowId: (log: TTrafficLog): number => log.log_id,
            fields: [],
            singleactions,
            expandedContentTemplateRef: this.expandedSummaryContentTemplateRef,
        };
    }

    /**
     * Set list grid fields.
     */
    private setDataGridFields(
        vsAppProfileType: VsAppProfileType,
        hasWafPolicy: boolean,
        hasTcpProxyNetProfile: boolean,
    ): void {
        // Set individual AviDataGrid fields to simplify L4, L7, and DNS grid combinations
        const timestampField: IAviDataGridConfigField = {
            id: 'timestamp',
            sortBy: 'report_timestamp',
            label: this.l10nService.getMessage(l10nKeys.timestampLabel),
            transform: (log: TTrafficLog): string => (
                log.report_timestamp ?
                    this.aviDatePipe.transform(log.report_timestamp, 'MM/dd, h:mm:ss a') :
                    '-'
            ),
        };

        const wafField: IAviDataGridConfigField = {
            id: 'waf',
            label: this.l10nService.getMessage(l10nKeys.wafLabel),
            templateRef: this.wafFieldTemplateRef,
        };

        const clientIpField: IAviDataGridConfigField = {
            id: 'client-ip',
            sortBy: 'client_ip',
            label: this.l10nService.getMessage(globalL10nKeys.clientIpLabel),
            templateRef: this.clientIpTemplateRef,
        };

        const clientPortField: IAviDataGridConfigField = {
            id: 'client-port',
            sortBy: 'client_src_port',
            label: this.l10nService.getMessage(l10nKeys.clientPortLabel),
            templateRef: this.clientPortTemplateRef,
        };

        const serverIpField: IAviDataGridConfigField = {
            id: 'server-ip',
            sortBy: 'server_ip',
            label: this.l10nService.getMessage(globalL10nKeys.serverIpLabel),
            templateRef: this.serverIpTemplateRef,
        };

        const serverPortField: IAviDataGridConfigField = {
            id: 'server-port',
            sortBy: 'server_dest_port',
            label: this.l10nService.getMessage(l10nKeys.serverPortLabel),
            templateRef: this.serverPortTemplateRef,
        };

        const uriField: IAviDataGridConfigField = {
            id: 'uri',
            sortBy: 'uri_path',
            label: this.l10nService.getMessage(l10nKeys.uriLabel),
            templateRef: this.uriTemplateRef,
        };

        const protocolField: IAviDataGridConfigField = {
            id: 'protocol',
            label: this.l10nService.getMessage(globalL10nKeys.protocolLabel),
            templateRef: this.protocolTemplateRef,
        };

        const dnsRequestTypeField: IAviDataGridConfigField = {
            id: 'dns-request-type',
            label: this.l10nService.getMessage(l10nKeys.dnsRequestTypeLabel),
            templateRef: this.dnsRequestTypeTemplateRef,
        };

        const domainNameField: IAviDataGridConfigField = {
            id: 'domain-name',
            label: this.l10nService.getMessage(l10nKeys.domainNameLabel),
            templateRef: this.domainNameTemplateRef,
        };

        const dnsResponseField: IAviDataGridConfigField = {
            id: 'dns-response',
            label: this.l10nService.getMessage(globalL10nKeys.responseLabel),
            templateRef: this.dnsResponseTemplateRef,
        };

        const requestField: IAviDataGridConfigField = {
            id: 'request',
            sortBy: 'method',
            label: this.l10nService.getMessage(globalL10nKeys.requestLabel),
            templateRef: this.requestTemplateRef,
        };

        const responseField: IAviDataGridConfigField = {
            id: 'response',
            sortBy: 'response_code',
            label: this.l10nService.getMessage(globalL10nKeys.responseLabel),
            templateRef: this.responseTemplateRef,
        };

        const lengthField: IAviDataGridConfigField = {
            id: 'length',
            sortBy: 'response_length',
            label: this.l10nService.getMessage(l10nKeys.lengthLabel),
            templateRef: this.lengthTemplateRef,
        };

        const totalBytesField: IAviDataGridConfigField = {
            id: 'total-bytes',
            sortBy: 'total_bytes',
            label: this.l10nService.getMessage(l10nKeys.totalBytesLabel),
            templateRef: this.totalBytesTemplateRef,
        };

        const durationField: IAviDataGridConfigField = {
            id: 'duration',
            sortBy: 'total_time',
            label: this.l10nService.getMessage(l10nKeys.durationLabel),
            templateRef: this.durationTemplateRef,
            columnFilterTemplateRef: this.durationColumnFilterTemplateRef,
            columnFilterState: {
                label: this.selectedFilterLabelSubject$.asObservable(),
            },
        };

        const ednsSubnetField: IAviDataGridConfigField = {
            id: 'edns-subnet',
            label: this.l10nService.getMessage(l10nKeys.ednsSubnetLabel),
            visibility: AviDataGridFieldVisibility.OPTIONAL,
            templateRef: this.ednsSubnetTemplateRef,
        };

        // Set data grid fields based on VS App Profile Type.
        switch (vsAppProfileType) {
            case VsAppProfileType.L7:
                this.logsListGridConfig.fields = [
                    timestampField,
                    clientIpField,
                    uriField,
                    requestField,
                    responseField,
                    lengthField,
                    durationField,
                ];

                if (hasWafPolicy) {
                    this.logsListGridConfig.fields.splice(1, 0, wafField);
                }

                break;

            case VsAppProfileType.L4:
                this.logsListGridConfig.fields = [
                    timestampField,
                    clientIpField,
                    clientPortField,
                    serverIpField,
                    serverPortField,
                    totalBytesField,
                ];

                if (hasTcpProxyNetProfile) {
                    this.logsListGridConfig.fields.push(durationField);
                }

                break;

            case VsAppProfileType.DNS:
                this.logsListGridConfig.fields = [
                    timestampField,
                    clientIpField,
                    protocolField,
                    dnsRequestTypeField,
                    domainNameField,
                    dnsResponseField,
                    ednsSubnetField,
                    durationField,
                ];
                break;
        }
    }

    /**
     * Add the ID of a single log under inspection by a c-view as selected for highlighting purpose.
     */
    private highlightRowForCinematicView(log: TTrafficLog): void {
        this.trafficLogsStore.setSelectedLogs([getTrafficLogIdentifier(log)]);
    }
}
